import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { contact } from "@/data/contact";

const TOP_NAV = [
  { to: "/", label: "Home" },
  { to: "/company", label: "Our Company" },
  {
    to: "/divisions",
    label: "Our Divisions",
    dropdown: [
      { to: "/divisions/petroleum-energy", label: "Petroleum & Energy" },
      { to: "/divisions/precious-metals", label: "Precious Metals" },
    ],
  },
  { to: "/strategic-relationships", label: "Strategic Relationships" },
  { to: "/leadership", label: "Leadership" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [divisionsOpen, setDivisionsOpen] = useState(false);
  const { pathname } = useLocation();
  const ddRef = useRef(null);
  const drawerRef = useRef(null);

  // Keep the mobile drawer OUT of the keyboard/AT tree when closed.
  // Uses the standard `inert` attribute (widely supported; falls back to
  // aria-hidden alone if the runtime doesn't recognise it).
  useEffect(() => {
    const el = drawerRef.current;
    if (!el) return;
    if (drawerOpen) {
      el.removeAttribute("inert");
    } else {
      el.setAttribute("inert", "");
    }
  }, [drawerOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("gp-lock", drawerOpen);
    return () => document.body.classList.remove("gp-lock");
  }, [drawerOpen]);

  useEffect(() => {
    setDrawerOpen(false);
    setDivisionsOpen(false);
  }, [pathname]);

  // Keyboard: Escape closes the dropdown; click-outside also closes it
  useEffect(() => {
    if (!divisionsOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setDivisionsOpen(false);
    };
    const onClickOut = (e) => {
      if (ddRef.current && !ddRef.current.contains(e.target)) setDivisionsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClickOut);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClickOut);
    };
  }, [divisionsOpen]);

  const isDivisionsActive = pathname.startsWith("/divisions");
  const cp = contact.company_phone;

  return (
    <>
      <div className="gp-contact-strip" data-testid="contact-strip" aria-label="Direct contact">
        <a href={`tel:${contact.company_phone.tel}`} className="gp-contact-strip__link" data-testid="contact-strip-call">
          <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"><path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8 9.6a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z"/></svg>
          Company telephone {contact.company_phone.raw}
        </a>
        <span className="gp-contact-strip__sep" aria-hidden="true">|</span>
        <a href={`mailto:${contact.sales_email.address}`} className="gp-contact-strip__link gp-contact-strip__link--email" data-testid="contact-strip-sales">{contact.sales_email.address}</a>
        <span className="gp-contact-strip__sep gp-contact-strip__sep--admin" aria-hidden="true">|</span>
        <a href={`mailto:${contact.general_email.address}`} className="gp-contact-strip__link gp-contact-strip__link--admin" data-testid="contact-strip-admin">{contact.general_email.address}</a>
      </div>
      <header
        className={`gp-header ${scrolled || drawerOpen ? "gp-header--solid" : ""}`}
        data-testid="site-header"
      >
        <div className="gp-wrap gp-header__inner">
          <Logo />

          <nav className="gp-nav" aria-label="Primary" data-testid="site-nav-desktop">
            {TOP_NAV.map((item) => {
              if (item.dropdown) {
                return (
                  <div
                    key={item.to}
                    ref={ddRef}
                    className={`gp-dd ${divisionsOpen ? "gp-dd--open" : ""}`}
                    onMouseEnter={() => setDivisionsOpen(true)}
                    onMouseLeave={() => setDivisionsOpen(false)}
                  >
                    <NavLink
                      to={item.to}
                      end
                      className={({ isActive }) =>
                        `gp-nav__item ${isActive || isDivisionsActive ? "gp-nav__item--active" : ""}`
                      }
                      aria-expanded={divisionsOpen}
                      aria-haspopup="menu"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          // Enter still navigates (default). Space opens the panel.
                          if (e.key === " ") {
                            e.preventDefault();
                            setDivisionsOpen((v) => !v);
                          }
                        } else if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setDivisionsOpen(true);
                        } else if (e.key === "Escape") {
                          setDivisionsOpen(false);
                        }
                      }}
                      data-testid={`nav-${slug(item.label)}`}
                    >
                      {item.label}
                      <span className="gp-nav__caret" aria-hidden="true" />
                    </NavLink>
                    <div className="gp-dd__panel" role="menu" aria-label={item.label}>
                      {item.dropdown.map((sub) => (
                        <NavLink
                          key={sub.to}
                          to={sub.to}
                          className="gp-dd__link"
                          role="menuitem"
                          data-testid={`nav-${slug(sub.label)}`}
                        >
                          {sub.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `gp-nav__item ${isActive ? "gp-nav__item--active" : ""}`
                  }
                  data-testid={`nav-${slug(item.label)}`}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <Link to="/contact" className="gp-btn gp-header__cta" data-testid="header-cta-contact">
            Contact <span className="gp-arrow" aria-hidden="true">→</span>
          </Link>

          <a
            href={`tel:${cp.tel}`}
            className="gp-header__mobile-call"
            aria-label="Call our team"
            data-testid="mobile-header-call"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8 9.6a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z"/>
            </svg>
          </a>

          <button
            className="gp-burger"
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
            data-testid="mobile-nav-open"
          >
            <span className="gp-burger__lines" aria-hidden="true">
              <span /><span /><span />
            </span>
          </button>
        </div>
      </header>

      <div
        ref={drawerRef}
        className={`gp-drawer ${drawerOpen ? "gp-drawer--open" : ""}`}
        aria-hidden={!drawerOpen}
        data-testid="mobile-drawer"
      >
        <div className="gp-drawer__head">
          <Logo />
          <button
            className="gp-drawer__close"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            data-testid="mobile-nav-close"
          >
            ×
          </button>
        </div>
        <div className="gp-drawer__body">
          <ul className="gp-drawer__list">
            {TOP_NAV.map((item) => {
              if (item.dropdown) {
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end
                      className={({ isActive }) =>
                        `gp-drawer__link ${isActive || isDivisionsActive ? "gp-drawer__link--active" : ""}`
                      }
                      data-testid={`mobile-nav-${slug(item.label)}`}
                    >
                      {item.label}
                    </NavLink>
                    <div className="gp-drawer__sub">
                      {item.dropdown.map((sub) => (
                        <NavLink
                          key={sub.to}
                          to={sub.to}
                          className={({ isActive }) =>
                            `gp-drawer__sublink ${isActive ? "gp-drawer__sublink--active" : ""}`
                          }
                          data-testid={`mobile-nav-${slug(sub.label)}`}
                        >
                          {sub.label}
                        </NavLink>
                      ))}
                    </div>
                  </li>
                );
              }
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      `gp-drawer__link ${isActive ? "gp-drawer__link--active" : ""}`
                    }
                    data-testid={`mobile-nav-${slug(item.label)}`}
                  >
                    {item.label}
                  </NavLink>
                </li>
              );
            })}
            <li>
              <NavLink to="/careers" className="gp-drawer__link" data-testid="mobile-nav-careers">
                Careers
              </NavLink>
            </li>
          </ul>
          <div className="gp-drawer__foot">
            <div>General enquiries</div>
            <a href="mailto:sales@gpfzllc.com">sales@gpfzllc.com</a>
          </div>
        </div>
      </div>
    </>
  );
}

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
